import React from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import {
  DatelessCTA,
  DatelessCTAVariant
} from 'components/Accommodation/Offers/DatelessCTA/DatelessCTA'
import {useAccommodationTopOffers} from 'components/Accommodation/Offers/useAccommodationTopOffers'
import {priceSuffixMessages} from 'components/Accommodation/Rooms/RoomOfferPrice/constants'
import {OfferPrice} from 'components/Offer/Shared/OfferPrice'
import {useGetHotelDetails} from 'hooks/useGetHotelDetails'
import {useIsraeliVatEligibility} from 'hooks/useIsraeliVatEligibility'
import {useOfferContainer} from 'hooks/useOfferContainer'
import {useOtaMode} from 'hooks/useOtaMode'
import {getHasRoomsSearchErrored} from 'modules/accommodation/selectors'
import {getAllTaxesIncluded} from 'modules/meta/selectors'
import {
  getShowTotalPrices,
  getSplitBookingDetails
} from 'modules/sapiSearch/selectors'
import {getIsNoDatesSearch} from 'modules/search/selectors'
import {
  getIsRoomsPollingComplete,
  getIsRoomsQueryLoading,
  getRoomsResult
} from 'modules/searchApi/selectors'

import {Box} from '@vio/atlas/helpers/Box'
import {SkeletonLoader} from '@vio/atlas/SkeletonLoader'
import {Button} from '@vio/atlas/src/components/designSystem/Button'
import {Text} from '@vio/atlas/Text'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

interface Props {
  hotelId: string
  onViewDealsClick: () => void
}

export const TopOfferCtaDesktopLoading = ({
  showPrice = true
}: {
  showPrice?: boolean
} = {}) => (
  <Box
    display="flex"
    alignItems="flex-start"
    gap="s500"
    paddingTop="s250"
    paddingBottom="s250"
    flex="0 0 auto"
    data-id="TopOfferCtaDesktopLoading"
  >
    {showPrice && (
      <Box display="flex" flexDirection="column" alignItems="flex-end">
        <SkeletonLoader width={90} />
        <SkeletonLoader width={120} />
      </Box>
    )}
    <SkeletonLoader width={200} marginTop={0} />
  </Box>
)

export const TopOfferCtaDesktop: React.FC<Props> = ({
  hotelId,
  onViewDealsClick
}) => {
  const {formatMessage} = useIntl()
  const {isOTAMode} = useOtaMode()
  const {offerEntity, isComplete} = useAccommodationTopOffers({hotelId})
  const {data: hotel} = useGetHotelDetails(hotelId)
  const {primaryOffer, anchorPrice, anchorPriceTotal} = useOfferContainer({
    offerEntity,
    hotelId
  })
  const {hasAdditionalCharges} = useIsraeliVatEligibility(hotelId)
  const showTotalPrices = useSelector(getShowTotalPrices)
  const isNoDatesSearch = useSelector(getIsNoDatesSearch)

  const {hasSplitBookingOffer, splitBookingPosition} = useSelector(state =>
    getSplitBookingDetails(state, hotelId)
  )

  const includeTaxes = useSelector(getAllTaxesIncluded)
  const isRoomsQueryLoading = useSelector(getIsRoomsQueryLoading)
  const isRoomsPollingComplete = useSelector(getIsRoomsPollingComplete)
  const hasRoomsErrored = useSelector(state =>
    getHasRoomsSearchErrored(state, hotelId)
  )
  const hasRoomsOrSplitBooking = useSelector(state => {
    const {rooms, splitBooking} = getRoomsResult(state).data || {}
    return Boolean(rooms?.length || splitBooking)
  })

  const isSplitBookingCheapest =
    hasSplitBookingOffer && splitBookingPosition === 0

  if (isNoDatesSearch && hotel)
    return (
      <DatelessCTA
        variant={DatelessCTAVariant.DesktopHeader}
        priceFrom={hotel.pricing?.minRateInUserCurrency}
        hotelId={hotel.objectID}
      />
    )

  if (isOTAMode) {
    const isWaitingForRoomsOrSplitBooking =
      !hasRoomsOrSplitBooking &&
      !hasRoomsErrored &&
      (isRoomsQueryLoading || !isRoomsPollingComplete)

    if (isWaitingForRoomsOrSplitBooking)
      return <TopOfferCtaDesktopLoading showPrice={false} />
    if (!hasRoomsOrSplitBooking) return null

    return (
      <Box
        display="flex"
        alignItems="center"
        paddingTop="s250"
        paddingBottom="s250"
        flex="0 0 auto"
        data-id="TopOfferCtaDesktop"
      >
        <Button
          variant="primary"
          size="xl"
          onClick={onViewDealsClick}
          dataId="TopOfferCtaDesktopButton"
        >
          <FormattedMessageWrapper id="viewRooms" defaultMessage="View rooms" />
        </Button>
      </Box>
    )
  }

  if (!isComplete) return <TopOfferCtaDesktopLoading />
  if (!primaryOffer) return null

  return (
    <Box
      display="flex"
      alignItems="center"
      gap="s500"
      paddingTop="s250"
      paddingBottom="s250"
      flex="0 0 auto"
      data-id="TopOfferCtaDesktop"
    >
      <Box display="flex" flexDirection="column" alignItems="flex-end">
        <Box display="flex" alignItems="baseline" gap="s200">
          <OfferPrice
            offer={primaryOffer}
            hotelId={hotelId}
            anchorPrice={anchorPrice}
            anchorPriceTotal={anchorPriceTotal}
            showAnchorPrice={false}
            isPrivateDeal={false}
            showPriceIndication={false}
            isSplitBooking={isSplitBookingCheapest}
            size="lg"
          />
          <Text variant="bodyS">
            {showTotalPrices
              ? formatMessage(priceSuffixMessages.totalV2)
              : formatMessage(priceSuffixMessages.perNight)}
          </Text>
        </Box>
        <Text variant="bodyS" dataId="includingTaxes">
          {hasAdditionalCharges
            ? formatMessage(priceSuffixMessages.additionalCharges)
            : includeTaxes || showTotalPrices
              ? formatMessage(priceSuffixMessages.withTaxesAndFees)
              : formatMessage(priceSuffixMessages.includingTaxes)}
        </Text>
      </Box>
      <Button
        variant="primary"
        size="xl"
        onClick={onViewDealsClick}
        dataId="TopOfferCtaDesktopButton"
      >
        <FormattedMessageWrapper id="viewDeals" defaultMessage="View deals" />
      </Button>
    </Box>
  )
}
